import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { X } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn.js";
import { IconButton } from "./icon-button.js";
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
/**
 * A genuine, blocking modal — `aria-modal="true"`, background content
 * inert to interaction, a focus trap, and focus restored to whatever
 * triggered it on close. This is deliberately a *different* primitive
 * from `SidePanel` (`aria-modal="false"`, non-blocking): docs/design.md
 * §1.3/§8.13 ban modals from every live-critical flow and most
 * create/edit forms (`SidePanel` covers those), but a small, real set of
 * interactions — a destructive confirmation, a one-off system prompt
 * outside Live Mode — are exactly what a true modal is for (§7.19's own
 * "danger zone" carve-out). Reach for `SidePanel` first; reach for this
 * only when the interaction must fully block the rest of the UI.
 */
export function Dialog({ open, onClose, title, children, footer, widthClassName = "max-w-md", preventDismiss = false, }) {
    const dialogRef = React.useRef(null);
    const bodyRef = React.useRef(null);
    const previouslyFocused = React.useRef(null);
    React.useEffect(() => {
        if (!open)
            return;
        previouslyFocused.current = document.activeElement;
        const dialogEl = dialogRef.current;
        // Initial focus is scoped to the body/footer only — never the header's
        // own close (X) button, which would otherwise win as "first in the DOM"
        // and put focus on the dismiss action before the actual content.
        const firstFocusable = bodyRef.current?.querySelector(FOCUSABLE_SELECTOR);
        (firstFocusable ?? dialogEl)?.focus();
        function handleKeyDown(event) {
            if (event.key === "Escape" && !preventDismiss) {
                onClose();
                return;
            }
            if (event.key !== "Tab" || !dialogEl)
                return;
            const focusable = Array.from(dialogEl.querySelectorAll(FOCUSABLE_SELECTOR));
            if (focusable.length === 0)
                return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            previouslyFocused.current?.focus();
        };
    }, [open, onClose, preventDismiss]);
    if (!open)
        return null;
    return createPortal(_jsxs("div", { className: "fixed inset-0 z-palette flex items-center justify-center p-4", children: [preventDismiss ? (_jsx("div", { className: "absolute inset-0 bg-black/60", "aria-hidden": "true" })) : (_jsx("button", { type: "button", "aria-label": "Close dialog", tabIndex: -1, onClick: onClose, className: "absolute inset-0 bg-black/60" })), _jsxs("div", { ref: dialogRef, role: "dialog", "aria-modal": "true", "aria-labelledby": "dialog-title", tabIndex: -1, className: cn("relative flex w-full flex-col rounded-lg border border-line bg-surface-raised shadow-lg outline-none", "motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:duration-base", widthClassName), children: [_jsxs("div", { className: "flex h-12 shrink-0 items-center justify-between border-b border-line-subtle px-4", children: [_jsx("h2", { id: "dialog-title", className: "text-h3 text-fg-primary", children: title }), preventDismiss ? null : (_jsx(IconButton, { "aria-label": "Close dialog", onClick: onClose, children: _jsx(X, {}) }))] }), _jsxs("div", { ref: bodyRef, className: "flex flex-1 flex-col overflow-hidden", children: [_jsx("div", { className: "flex-1 overflow-y-auto px-4 py-4", children: children }), footer ? (_jsx("div", { className: "flex shrink-0 items-center justify-end gap-2 border-t border-line-subtle px-4 py-3", children: footer })) : null] })] })] }), document.body);
}
//# sourceMappingURL=dialog.js.map